import React from 'react';
import {
    ContinuantStatRecord,
    GraphStatistics,
    GroupedContinuantsMap,
    ProcessParamStatRecord,
    ProcessStatRecord
} from '../interface';
import {getProcessId, getProcessParamId} from '../get-entity-id';

interface Props {
    stats?: GraphStatistics;
    searchString: string;
}

// when performing search, we should separate matched children of each folding el from irrelevant
export interface SearchResultChildrenMaps {
    continuantGroupsMatchedChildren: {[key: string]: ContinuantStatRecord[]};
    continuantGroupsIrrelevantChildren: {[key: string]: ContinuantStatRecord[]};

    processesMatchedChildren: {[key: string]: ProcessParamStatRecord[]};
    processesIrrelevantChildren: {[key: string]: ProcessParamStatRecord[]};

    processParamsMatchedChildren: {[key: string]: ContinuantStatRecord[]};
    processParamsIrrelevantChildren: {[key: string]: ContinuantStatRecord[]};
}

interface SearchResult {
    filteredContinuantsNumber: number;
    filteredProcessesNumber: number;
    hasSearchResults: boolean;
    isNothingFound: boolean;
    filteredContinuantsGroups?: GroupedContinuantsMap;
    filteredProcesses?: ProcessStatRecord[];
    searchResultChildrenMaps?: SearchResultChildrenMaps;
}

export function matchString(entityName: string, searchString: string) {
    return !!entityName
        .toLowerCase()
        .match(searchString.toLowerCase().replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
}

function matchContinuant(searchString: string, continuant: ContinuantStatRecord) {
    return (
        matchString(continuant.label, searchString) ||
        !!continuant.synonyms.find((synonym) => matchString(synonym, searchString))
    );
}

export function matchContinuants(searchString: string, continuants: ContinuantStatRecord[]) {
    const [matchedContinuants, irrelevantContinuants] = [
        [] as ContinuantStatRecord[],
        [] as ContinuantStatRecord[]
    ];

    continuants.forEach((continuant) =>
        matchContinuant(searchString, continuant)
            ? matchedContinuants.push(continuant)
            : irrelevantContinuants.push(continuant)
    );

    return [matchedContinuants, irrelevantContinuants];
}

export function useFilterStats(props: Props): SearchResult {
    return React.useMemo((): SearchResult => {
        if (!props.stats || !props.searchString) {
            return {
                filteredContinuantsNumber: 0,
                filteredProcessesNumber: 0,
                filteredContinuantsGroups: props.stats?.groupedContinuantsMap,
                filteredProcesses: props.stats?.processes,
                hasSearchResults: false,
                isNothingFound: false
            };
        }

        let filteredContinuantsNumber = 0;
        const filteredContinuantsGroups: GroupedContinuantsMap = {};

        const searchResultChildrenMaps: SearchResultChildrenMaps = {
            continuantGroupsMatchedChildren: {},
            continuantGroupsIrrelevantChildren: {},

            processesMatchedChildren: {},
            processesIrrelevantChildren: {},

            processParamsMatchedChildren: {},
            processParamsIrrelevantChildren: {}
        };

        Object.keys(props.stats.groupedContinuantsMap).forEach((continuantsGroup) => {
            const isGroupNameMatches = matchString(continuantsGroup, props.searchString);
            const continuants = props.stats!.groupedContinuantsMap[continuantsGroup];

            const [matchedContinuants, irrelevantContinuants] = matchContinuants(
                props.searchString,
                continuants
            );

            if (isGroupNameMatches) {
                filteredContinuantsNumber++;
            }
            filteredContinuantsNumber += matchedContinuants.length;

            searchResultChildrenMaps.continuantGroupsMatchedChildren[continuantsGroup] =
                matchedContinuants;
            searchResultChildrenMaps.continuantGroupsIrrelevantChildren[continuantsGroup] =
                irrelevantContinuants;

            if (isGroupNameMatches || matchedContinuants.length) {
                filteredContinuantsGroups[continuantsGroup] = continuants;
            }
        });

        let filteredProcessesNumber = 0;
        const filteredProcesses = props.stats.processes.filter((process) => {
            const isProcessNameMatches = matchString(process.label, props.searchString);
            if (isProcessNameMatches) {
                filteredProcessesNumber++;
            }

            const filteredParams = process.params.filter((param) => {
                const isParamNameMatches = matchString(param.label, props.searchString);
                const [matchedParamValues, irrelevantParamValues] = matchContinuants(
                    props.searchString,
                    param.values
                );

                if (isParamNameMatches) {
                    filteredProcessesNumber++;
                }
                filteredProcessesNumber += matchedParamValues.length;

                searchResultChildrenMaps.processParamsMatchedChildren[
                    getProcessParamId(process, param)
                ] = matchedParamValues;
                searchResultChildrenMaps.processParamsIrrelevantChildren[
                    getProcessParamId(process, param)
                ] = irrelevantParamValues;

                if (!!matchedParamValues.length || isParamNameMatches) {
                    // add param to the matched children of the process
                    searchResultChildrenMaps.processesMatchedChildren[getProcessId(process)] = [
                        ...(searchResultChildrenMaps.processesMatchedChildren[
                            getProcessId(process)
                        ] || []),
                        param
                    ];
                } else {
                    // add param to the irrelevant children of the process
                    searchResultChildrenMaps.processesIrrelevantChildren[getProcessId(process)] = [
                        ...(searchResultChildrenMaps.processesIrrelevantChildren[
                            getProcessId(process)
                        ] || []),
                        param
                    ];
                }

                return isParamNameMatches || matchedParamValues.length;
            });

            return isProcessNameMatches || filteredParams.length;
        });

        const isNothingFound = filteredProcessesNumber === 0 && filteredContinuantsNumber === 0;

        return {
            filteredContinuantsNumber,
            filteredProcessesNumber,
            filteredContinuantsGroups,
            filteredProcesses,
            hasSearchResults: !isNothingFound,
            isNothingFound,
            searchResultChildrenMaps
        };
    }, [props.stats, props.searchString]);
}
